import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { ArrowRight, Waves, Check } from 'lucide-react';

const WHATSAPP_URL = 'https://wa.me/message/AIKG7VFJUUNMJ1';

const IMAGES = {
  janzu: 'https://customer-assets.emergentagent.com/job_abba-wellness-cancun/artifacts/uvlaxb1p_Janzu.jpg',
  janzuSea: 'https://customer-assets.emergentagent.com/job_5bf56e5f-42e0-4cfb-a653-edc31a7a571c/artifacts/oq20ui19_Janzu.jpg',
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

export const Janzu = () => {
  const { t } = useLanguage();

  return (
    <div data-testid="janzu-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 md:pt-40 md:pb-32 min-h-[80vh] flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.janzu}
            alt="Janzu Water Therapy"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-2xl"
          >
            <motion.div variants={fadeInUp} className="flex items-center gap-3 mb-6">
              <Waves className="w-8 h-8 text-[#C9B99A]" />
              <span className="text-sm uppercase tracking-[0.2em] text-[#C9B99A] font-['Manrope'] font-semibold">
                {t('janzu.subtitle')}
              </span>
            </motion.div>
            <motion.h1 variants={fadeInUp} className="font-['Playfair_Display'] text-5xl sm:text-6xl lg:text-7xl text-white mb-6 tracking-tight">
              {t('janzu.title')}
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-lg md:text-xl text-white/80 mb-8 font-['Manrope']">
              {t('janzu.description')}
            </motion.p>
            
            <motion.div variants={fadeInUp}>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#C9B99A] hover:bg-[#C49366] text-white rounded-full px-8 py-6 font-['Manrope'] font-semibold">
                  {t('cta.contact')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 md:py-32 px-6" data-testid="janzu-benefits">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#1C1C1C] mb-8 tracking-tight">
                Float into <em className="italic text-[#A91B60]">stillness</em>
              </motion.h2>
              
              <motion.div variants={fadeInUp} className="space-y-4">
                {t('janzu.benefits').map((benefit, index) => (
                  <div key={index} className="flex items-center gap-4 p-4 bg-[#F8F6F3] rounded-xl">
                    <div className="w-8 h-8 rounded-full bg-[#A91B60] flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-lg text-[#1C1C1C] font-['Manrope']">{benefit}</span>
                  </div>
                ))}
              </motion.div>
              
              <motion.div variants={fadeInUp} className="mt-8">
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-[#A91B60] hover:bg-[#8A1650] text-white rounded-full px-8 py-6 font-['Manrope'] font-semibold">
                    {t('cta.contact')}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </a>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="absolute -top-4 -right-4 w-full h-full bg-[#A91B60]/20 rounded-2xl" />
              <img
                src={IMAGES.janzuSea}
                alt="Janzu in the Sea"
                className="relative rounded-2xl shadow-xl w-full aspect-square object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#A91B60]">
        <div className="max-w-3xl mx-auto text-center px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="mb-6">
              <Waves className="w-12 h-12 text-[#C9B99A] mx-auto" />
            </motion.div>
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl md:text-5xl text-white mb-6 tracking-tight">
              Experience the <em className="italic text-[#C9B99A]">water</em>
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-white/80 mb-10 font-['Manrope']">
              Let the water carry your stress away. Book your Janzu session today.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#C9B99A] hover:bg-[#C49366] text-white rounded-full px-10 py-6 font-['Manrope'] font-semibold">
                  {t('cta.contact')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Janzu;
